import { Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { XStack, YStack } from 'tamagui';
import { useThemeColors, Icon, dropShadow } from '@kiips/ui';
import { useMenu } from '../../contexts/MenuContext';
import { KiipsLogo } from './KiipsLogo';

const BAR_CONTENT_HEIGHT = 56;

interface AppTopBarProps {
  /**
   * Content rendered between the kiips wordmark and the hamburger menu.
   * Use this slot for per-screen contextual actions (layers, edit, export…)
   * or the "My trips" link on the landing page.
   */
  rightSlot?: React.ReactNode | undefined;
  /**
   * Show a back arrow to the left of the kiips wordmark. Calls
   * `router.back()` on press.
   */
  showBack?: boolean | undefined;
}

/**
 * Unified top bar for every screen. Renders inside each hero image so
 * the logo + actions visually sit within the image boundary.
 *
 * Left  : optional back arrow + kiips wordmark (logo taps to /)
 * Middle: rightSlot (optional contextual buttons passed by the screen)
 * Right : hamburger (global menu — profile, theme, sign-out, etc.)
 *
 * For non-hero screens (trips list, import, profile…) ResponsivePage
 * renders this at the page level so it stays fixed above the scroll.
 */
export function AppTopBar({ rightSlot, showBack = false }: AppTopBarProps = {}) {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const theme = useThemeColors();
  const { toggle } = useMenu();

  const btnStyle = {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.surface,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    ...dropShadow({ offsetY: 2, radius: 6, opacity: 0.08, color: theme.shadowDefault, elevation: 3 }),
  };

  return (
    <XStack
      position="absolute"
      top={0}
      left={0}
      right={0}
      paddingTop={insets.top}
      height={insets.top + BAR_CONTENT_HEIGHT}
      paddingHorizontal={16}
      alignItems="center"
      gap={8}
      zIndex={50}
      pointerEvents="box-none"
    >
      {showBack ? (
        <Pressable
          onPress={() => {
            if (router.canGoBack()) router.back();
            else router.push('/');
          }}
          accessibilityLabel="Back"
          style={({ pressed }) => [btnStyle, pressed && { opacity: 0.8 }]}
        >
          <Icon name="arrow-back" size={20} color={theme.textOnSurface} />
        </Pressable>
      ) : null}
      <Pressable
        onPress={() => router.push('/')}
        accessibilityLabel="Home"
        style={({ pressed }) => [
          btnStyle,
          { paddingHorizontal: 14, width: 'auto' },
          pressed && { opacity: 0.8 },
        ]}
      >
        <KiipsLogo height={20} accessibilityLabel="kiips home" />
      </Pressable>

      <YStack flex={1} />

      {rightSlot ? (
        <XStack alignItems="center" gap={8} pointerEvents="box-none">
          {rightSlot}
        </XStack>
      ) : null}

      <Pressable
        onPress={toggle}
        accessibilityRole="button"
        accessibilityLabel="Menu"
        style={({ pressed }) => [btnStyle, { marginLeft: 8 }, pressed && { opacity: 0.8 }]}
      >
        <Icon name="menu" size={20} color={theme.textOnSurface} />
      </Pressable>
    </XStack>
  );
}

/** Total bar height including safe-area inset. */
export function useAppTopBarHeight(): number {
  const insets = useSafeAreaInsets();
  return insets.top + BAR_CONTENT_HEIGHT;
}
